import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import rootReducer from '@/store/rootReducer';
import { useFieldArrayId } from '@/utils/formatter';

type RootState = ReturnType<typeof rootReducer>;

const initialState: Venue.FormData = {
  clientId: null,
  venueName: '',
  inheritsClient: false,
  companyRegistrationNumber: '',
  ceoName: '',
  businessCategory: '',
  contractBegin: '',
  contractEnd: '',
  clientVenueManagers: [],
  operatorManagers: [],
  address: '',
  workCategoryPresetId: null,
};

const venueFormStore = createSlice({
  name: 'venueForm',
  initialState: initialState,
  reducers: {
    /**
     * 지점 폼 상태의 값을 변경한다.
     */
    setVenueFormData: (
      state,
      { payload }: PayloadAction<Partial<Venue.FormData>>
    ) => {
      state = {
        ...state,
        ...payload,
      };
      return state;
    },
    /**
     * 지점 폼 상태의 값을 초기화
     */
    resetVenueFormData: (state) => {
      state = initialState;
      return state;
    },

    /**
     * 고객사의 정보를 통해 지점의 일부정보를 상속받는다.
     */
    setClientInheritedData: (
      state,
      { payload }: PayloadAction<Client.Detail>
    ) => {
      state['clientId'] = payload.id;
      state['companyRegistrationNumber'] =
        payload?.companyRegistrationNumber || '';
      state['ceoName'] = payload?.ceoName || '';
      state['businessCategory'] = payload?.businessCategory || '';

      return state;
    },
    /**
     * (Legacy) 값이 Array인 상태를 강제로 업데이트
     */
    hardSetVenueArrayData: (
      state,
      { payload: { id, value } }: PayloadAction<{ id: string; value: unknown }>
    ) => {
      state[id] = value;
      return state;
    },

    /**
     * 상태값이 배열의 형식인 경우, 배열의 특정 아이템 객체 내의 특정 키에 대한 값 업데이트
     */
    setVenueArrayData: (
      state,
      { payload: { id, value } }: PayloadAction<{ id: string; value: unknown }>
    ) => {
      const { name, index, key } = useFieldArrayId(id);
      if (state[name][index]) {
        state[name][index][key] = value;
      } else {
        state[name][index] = { [key]: value };
      }
      return state;
    },

    /**
     * 상태값이 배열의 형식인 경우, 배열에 새로운 아이템을 추가
     */
    createVenueArrayData: (
      state,
      { payload: { id, value } }: PayloadAction<{ id: string; value: unknown }>
    ) => {
      state[id].push(value);
      return state;
    },

    /**
     * 상태값이 배열의 형식인 경우, 배열의 특정 아이템을 삭제
     */
    removeVenueArrayData: (
      state,
      { payload: { id } }: PayloadAction<{ id: string }>
    ) => {
      const { name, index } = useFieldArrayId(id);
      state[name].splice(index, 1);
      return state;
    },
  },
});

export const venueFormDataSelector = (state: RootState) => state.venue;

export const venueFormStoreActions = venueFormStore.actions;

export default venueFormStore.reducer;
